import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/Badge";
import { JOB_ROLES, ROLE_LABEL, label } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, Pencil, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const PERM_ROLES = [
  { v: "employee", l: "Employee" },
  { v: "manager", l: "Manager" },
  { v: "super_admin", l: "CEO / Super Admin" },
];

function MemberDialog({ open, onOpenChange, initial, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", role: "employee", job_role: "designer", department: "", password: "password123", active: true, can_view_payments: false });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  React.useEffect(() => {
    setF(initial ? { name: initial.name || "", email: initial.email || "", phone: initial.phone || "", role: initial.role, job_role: initial.job_role || "other", department: initial.department || "", password: "", active: initial.active, can_view_payments: initial.can_view_payments || false } : { name: "", email: "", phone: "", role: "employee", job_role: "designer", department: "", password: "password123", active: true, can_view_payments: false });
  }, [initial, open]);

  const save = async () => {
    if (!f.name || !f.email) return toast.error("Name and email required");
    setSaving(true);
    try {
      if (initial?.id) {
        const payload = { name: f.name, phone: f.phone, role: f.role, job_role: f.job_role, department: f.department, active: f.active, can_view_payments: f.can_view_payments };
        if (f.password) payload.password = f.password;
        await api.put(`/team/${initial.id}`, payload);
      } else {
        await api.post("/team", f);
      }
      toast.success(initial?.id ? "Member updated" : "Member added");
      onOpenChange(false); onSaved && onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Edit Employee" : "Add Employee"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs text-zinc-500">Employee Name *</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1" data-testid="member-name-input" /></div>
          <div><Label className="text-xs text-zinc-500">Email / Username *</Label><Input value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-1" disabled={!!initial?.id} data-testid="member-email-input" /></div>
          <div><Label className="text-xs text-zinc-500">Mobile Number</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-500">Job Role</Label>
              <Select value={f.job_role} onValueChange={(v) => set("job_role", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{JOB_ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Access Level</Label>
              <Select value={f.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger className="mt-1" data-testid="member-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PERM_ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs text-zinc-500">Department</Label><Input value={f.department} onChange={(e) => set("department", e.target.value)} className="mt-1" /></div>
          <div className="flex items-center justify-between rounded-md border border-zinc-200 px-3 py-2.5">
            <div>
              <Label className="text-sm font-medium">View Payments</Label>
              <p className="text-[11px] text-zinc-400">Allow this employee to see billing & payment figures</p>
            </div>
            <Switch checked={f.can_view_payments} onCheckedChange={(v) => set("can_view_payments", v)} data-testid="member-payments-permission" />
          </div>
          <div><Label className="text-xs text-zinc-500">{initial?.id ? "Reset Password (optional)" : "Initial Password"}</Label><Input value={f.password} onChange={(e) => set("password", e.target.value)} className="mt-1" placeholder={initial?.id ? "Leave blank to keep" : ""} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="member-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MemberRow({ m, onEdit, onToggle }) {
  const [open, setOpen] = useState(false);
  const { data: stats } = useQuery({ queryKey: ["team-stats", m.id], queryFn: async () => (await api.get(`/team/${m.id}/stats`)).data, enabled: open });
  const initials = (m.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("");
  return (
    <div className="border-b border-zinc-100 last:border-0" data-testid={`team-row-${m.id}`}>
      <div className="flex items-center gap-3 px-5 py-3">
        <button onClick={() => setOpen(!open)} className="flex flex-1 items-center gap-3 text-left">
          <span className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold text-white" style={{ background: m.color || "#71717a" }}>{initials}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2"><span className="font-medium">{m.name}</span>{!m.active && <Badge color="grey">Inactive</Badge>}</div>
            <div className="text-xs text-zinc-500">{m.title || "—"} · {m.department || "—"} · {ROLE_LABEL[m.role]}</div>
          </div>
          <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <Button size="sm" variant="ghost" onClick={() => onEdit(m)} data-testid={`edit-member-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
        <Button size="sm" variant="outline" onClick={() => onToggle(m)} className={m.active ? "text-red-600" : "text-emerald-600"} data-testid={`toggle-member-${m.id}`}>{m.active ? "Deactivate" : "Activate"}</Button>
      </div>
      {open && (
        <div className="grid grid-cols-1 gap-4 bg-zinc-50 px-5 py-4 sm:grid-cols-2">
          <div className="rounded-md border border-zinc-200 bg-white p-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Content Work</div>
            {stats ? (
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div><div className="metric-value text-lg font-semibold">{stats.content.assigned}</div><div className="text-xs text-zinc-400">Assigned</div></div>
                <div><div className="metric-value text-lg font-semibold text-emerald-600">{stats.content.posted}</div><div className="text-xs text-zinc-400">Posted</div></div>
                <div><div className="metric-value text-lg font-semibold text-amber-600">{stats.content.pending}</div><div className="text-xs text-zinc-400">Pending</div></div>
              </div>
            ) : <Loader2 className="mx-auto h-4 w-4 animate-spin text-zinc-300" />}
          </div>
          <div className="rounded-md border border-zinc-200 bg-white p-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">General Tasks</div>
            {stats ? (
              <div className="grid grid-cols-4 gap-2 text-center text-sm">
                <div><div className="metric-value text-lg font-semibold">{stats.tasks.assigned}</div><div className="text-xs text-zinc-400">Assigned</div></div>
                <div><div className="metric-value text-lg font-semibold text-emerald-600">{stats.tasks.completed}</div><div className="text-xs text-zinc-400">Done</div></div>
                <div><div className="metric-value text-lg font-semibold text-amber-600">{stats.tasks.pending}</div><div className="text-xs text-zinc-400">Pending</div></div>
                <div><div className="metric-value text-lg font-semibold text-red-600">{stats.tasks.overdue}</div><div className="text-xs text-zinc-400">Overdue</div></div>
              </div>
            ) : <Loader2 className="mx-auto h-4 w-4 animate-spin text-zinc-300" />}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Team() {
  const qc = useQueryClient();
  const { refreshUsers } = useAuth();
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const { data: team = [], isLoading } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/team")).data });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["team"] }); refreshUsers(); };
  const toggle = async (m) => { await api.put(`/team/${m.id}`, { active: !m.active }); toast.success(m.active ? "Deactivated" : "Activated"); refresh(); };

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Team</h1>
          <p className="mt-1 text-sm text-zinc-500">Manage employees & accounts. Click a member to see both workstreams.</p>
        </div>
        <Button onClick={() => { setEditing(null); setDialog(true); }} data-testid="add-member-button"><Plus className="mr-2 h-4 w-4" />Add Employee</Button>
      </div>

      <Card className="overflow-hidden rounded-lg border-zinc-200 shadow-sm">
        {isLoading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
        ) : team.map((m) => <MemberRow key={m.id} m={m} onEdit={(x) => { setEditing(x); setDialog(true); }} onToggle={toggle} />)}
      </Card>

      <MemberDialog open={dialog} onOpenChange={setDialog} initial={editing} onSaved={refresh} />
    </div>
  );
}
